import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Button } from '@mui/material';
import { ThemeProvider as MuiThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { SnackbarProvider } from 'notistack';
import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import GlobalStyles from 'src/components/GlobalStyles';
import ScrollReset from 'src/components/ScrollReset';
import { AuthProvider } from 'src/contexts/AuthContext';
import useSettings from 'src/hooks/useSettings';
import AppRoutes from 'src/routes';
import { createTheme } from 'src/theme';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from './AppInsights';
import Rocketize from 'src/components/Rocketize';
import { RoleProvider } from './contexts/RoleContext';
import { SelectedClientsProvider } from './contexts/SelectedClientsContext';
import { ClientsProvider } from './contexts/ClientsContext';
import { RetailerCacheProvider } from './contexts/RetailerCacheContext';
import FeedbackWidget from './components/FeedbackWidget';
import RouteActivityTracker from './components/RouteActivityTracker';
import { BUILD_INFO } from './buildInfo';
import { ENVIRONMENT } from 'src/utilities/api-config';

const App = () => {
  const { settings } = useSettings();
  const notistackRef = React.createRef();
  const onClickDismiss = (key) => () => {
    notistackRef.current.closeSnackbar(key);
  };

  // Set page title based on environment
  React.useEffect(() => {
    const baseTitle = 'Console';
    if (ENVIRONMENT === 'Local') {
      document.title = `${baseTitle} - Local`;
    } else if (ENVIRONMENT === 'Test') {
      document.title = `${baseTitle} - Test`;
    } else {
      document.title = baseTitle;
    }
  }, []);

  // Log build information on app startup
  React.useEffect(() => {
    console.group('%c🚀 Lanternal Console - Build Information', 'color: #2196f3; font-size: 14px; font-weight: bold;');
    console.log('%cGit Commit:', 'font-weight: bold;', BUILD_INFO.gitCommitShortHash, `(${BUILD_INFO.gitBranch})`);
    console.log('%cFull Hash:', 'font-weight: bold;', BUILD_INFO.gitCommitHash);
    console.log('%cBuild Number:', 'font-weight: bold;', BUILD_INFO.buildNumber);
    console.log('%cBuild Time:', 'font-weight: bold;', new Date(BUILD_INFO.buildTimestamp).toLocaleString());
    console.groupEnd();
  }, []);

  const theme = createTheme({
    direction: settings.direction,
    responsiveFontSizes: settings.responsiveFontSizes,
    theme: settings.theme,
  });

  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <StyledEngineProvider injectFirst>
        <MuiThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            {/* I added pointerEvents according to a github issue that made buttons unclickable
              https://github.com/iamhosseindhv/notistack/issues/394 */}
            <SnackbarProvider
              dense
              maxSnack={5}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              style={{ pointerEvents: 'all' }}
              ref={notistackRef}
              action={(key) => (
                <Button color="inherit" onClick={onClickDismiss(key)}>
                  Dismiss
                </Button>
              )}
            >
              <RoleProvider>
                <ClientsProvider>
                  <RetailerCacheProvider>
                    <AuthProvider>
                      <BrowserRouter>
                        <SelectedClientsProvider>
                          <GlobalStyles />
                          <ScrollReset />
                          <RouteActivityTracker />
                          <AppRoutes />
                          <FeedbackWidget />
                          <Rocketize active />
                        </SelectedClientsProvider>
                      </BrowserRouter>
                    </AuthProvider>
                  </RetailerCacheProvider>
                </ClientsProvider>
              </RoleProvider>
            </SnackbarProvider>
          </LocalizationProvider>
        </MuiThemeProvider>
      </StyledEngineProvider>
    </AppInsightsContext.Provider>
  );
};

export default App;
